import { Button } from "@/components/ui/button";
import { appRoutes, RouteNames } from "@/shared/constants/routes";
import { t } from "@/shared/i18n";
import * as React from "react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Replace the default full-page fallback. */
  fallback?: React.ReactNode;
  /** Remount the boundary when this changes — e.g. the route path. */
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-time crashes so one broken screen never takes the whole site
 * down. A class component because React still has no hook equivalent — hence
 * the module-level `t()` rather than `useTranslation()`.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidUpdate(prev: ErrorBoundaryProps) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[boundary] uncaught render error", error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="container flex min-h-[60vh] flex-col items-start justify-center gap-6 py-24">
        <span aria-hidden className="h-px w-12 bg-kf-taupe" />
        <span className="font-heading text-eyebrow font-medium uppercase tracking-wide2 text-kf-muted">
          {t("errors.genericTitle")}
        </span>
        <h1 className="max-w-2xl">{t("errors.genericTitle")}</h1>
        <p className="max-w-prose text-body font-light text-kf-body">
          {t("errors.genericBody")}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <Button onClick={() => window.location.reload()}>
            {t("errors.reload")}
          </Button>
          <Button variant="link" asChild>
            <a href={appRoutes[RouteNames.landing].path}>{t("errors.goHome")}</a>
          </Button>
        </div>
        {import.meta.env.DEV ? (
          <details className="mt-8 w-full max-w-3xl border border-kf-line bg-kf-cream-light p-5">
            <summary className="cursor-pointer font-heading text-[11px] uppercase tracking-wide2 text-kf-secondary">
              {t("errors.details")}
            </summary>
            <pre className="mt-4 overflow-x-auto whitespace-pre-wrap text-[12px] text-kf-secondary">
              {error.stack || error.message}
            </pre>
          </details>
        ) : null}
      </div>
    );
  }
}

export default ErrorBoundary;
